import type { SolutionUserPresenceItem } from '../xstate/prostudio.xstate.types';

/** The two routes that render the editable node table. */
export function isTablePage(path: string): boolean {
  return path.includes('/workflows') || path.includes('/actions');
}

/**
 * PSTUDIO-3929 (b): the coworker-focus subscription is the ONLY cross-machine
 * live sync path. It used to open only while another user held a *focused
 * cell* on the same page, so a teammate who merely ADDED a node (without
 * keeping a cell focused) never pushed the new row — the receiver had to
 * reload manually. Gate on the weaker "another active user is on this same
 * table page" instead, so a newly added node propagates in near-real-time.
 *
 * `focusedCell` is still consumed by `getFocusSignature` to decide WHEN to
 * re-run validation on an incoming merge — only the SUBSCRIBE gate is widened
 * here, never the validation-trigger signature.
 */
export function hasOtherActiveUserOnPage(
  presences: SolutionUserPresenceItem[] | undefined,
  pathname: string,
  currentUserId: string
): boolean {
  if (!isTablePage(pathname)) return false;
  return (presences ?? []).some(
    (p) =>
      String(p.userId ?? p.user?.id ?? '') !== currentUserId &&
      Boolean(p.isActive && p.pagePath === pathname)
  );
}

/**
 * Whether the coworker subscription opens at all. Never while the table is fed
 * by the draft's collab document (PSTUDIO-4450): the document already carries
 * every coworker's edit, and a Hasura merge would overwrite its rows with the
 * database's.
 */
export function shouldOpenCoworkerSubscription({
  solutionVersionId,
  currentUserId,
  hasOtherActiveUser,
  liveEditingSource,
}: {
  solutionVersionId: string | undefined;
  currentUserId: string;
  hasOtherActiveUser: boolean;
  liveEditingSource: boolean;
}): boolean {
  return (
    !liveEditingSource &&
    Boolean(solutionVersionId) &&
    Boolean(currentUserId) &&
    hasOtherActiveUser
  );
}

/**
 * Whether a coworker push the subscription delivered may be merged. Closing
 * the subscription races messages urql has already queued, so the callback
 * re-checks what the gate checked: the version it was opened for must still be
 * the store's (PSTUDIO-3953), and the table must not have started taking its
 * rows from the collab document (PSTUDIO-4450) — a Hasura snapshot merged then
 * would write the database's rows over the document's.
 */
export function shouldApplyCoworkerPush({
  subscribedFor,
  storeVersion,
  liveEditingSource,
}: {
  subscribedFor: string | undefined;
  storeVersion: string | undefined;
  liveEditingSource: boolean;
}): boolean {
  return !liveEditingSource && storeVersion === subscribedFor;
}
